<template>
  <div>
    <PriorHeader />
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1 class="text-primary weight-600 pb-4">{{ pageTitle }}</h1>
          <div v-if="preloader">
            <SubmissionPageSkeleton />
          </div>
          <div v-else>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <SubmissionDetailCard :order="orderInfo" :buttons-visible="false" :fax-forms="faxDestinationOptions" />
              </v-col>
            </v-row>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <PolicyReviewCard :order="orderInfo" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" class="submission-detail-section">
                <AllFormStepsView
                  ref="allFormStepsRef"
                  :disabled="isDisabled"
                  :filled-form-pdf-blob="filledFormPdfBlob"
                  @send-to-prescriber="handleSendToPrescriber"
                  @send-to-plan="handleSendToPlan" />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="showFaxEditor || showEmailEditor">
        <v-col v-if="orderInfo" cols="12">
          <!-- Send Method Toggle (only shown when email is enabled) -->
          <v-btn-toggle
            v-if="sendEmailEnabled"
            v-model="sendMethod"
            mandatory
            class="mb-4"
            color="primary"
            variant="outlined">
            <v-btn value="fax">
              <v-icon start>mdi-fax</v-icon>
              Send Fax
            </v-btn>
            <v-btn value="email">
              <v-icon start>mdi-email</v-icon>
              Send Email
            </v-btn>
          </v-btn-toggle>

          <!-- Fax Editor -->
          <OutgoingFaxEditor
            v-if="sendMethod === 'fax'"
            ref="outgoingFaxEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-fax-number="currentFaxNumber"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="sendButtonText"
            :supporting-documents="supportingDocuments"
            title="Send Fax"
            @on-complete="onFaxComplete"
            @on-pre-send="saveOrderDraft"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />

          <!-- Email Editor -->
          <OutgoingEmailEditor
            v-if="sendMethod === 'email'"
            ref="outgoingEmailEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-emails="currentEmailAddresses"
            :default-subject="currentEmailSubject"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="emailSendButtonText"
            :supporting-documents="supportingDocuments"
            title="Send Email"
            @on-complete="onEmailComplete"
            @on-pre-send="saveOrderDraft"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />
        </v-col>
        <v-alert v-if="showFaxHelpText && sendMethod === 'fax'" type="info" variant="tonal" prominent class="mb-4">
          <template #prepend>
            <v-icon size="large">mdi-information</v-icon>
          </template>
          <div class="text-body-1 font-weight-bold">
            Please verify this is the correct fax number for the insurance provider where the prior authorization will
            be sent.
          </div>
        </v-alert>
      </v-row>

      <v-row>
        <v-col cols="12">
          <AuditLogList ref="auditLogListRef" :order-info-id="route.params.id" />
        </v-col>
      </v-row>

      <v-snackbar v-model="showSuccessNotification" color="success" timeout="5000" location="top">
        {{ successNotificationMessage }}
      </v-snackbar>
      <div v-if="errors.length > 0" class="mt-4">
        <v-alert v-for="error in errors" :key="error" type="error" variant="tonal" class="mb-2">
          {{ error }}
        </v-alert>
      </div>
    </v-container>
    <PriorFooter />
  </div>
</template>

<script setup>
import PriorFooter from "@/components/PriorFooter.vue";
import PriorHeader from "@/components/PriorHeader.vue";
import AllFormStepsView from "@/pages/AllFormStepsView";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";

import { onBeforeMount, ref, computed, nextTick } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { clearOrderInfoStorage, convertDateFormat, tryParseApiErrors } from "@/utils";

import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";
import { useOrderInfoStore } from "@/stores/orderInfoStore";

import PolicyReviewCard from "@/components/PolicyReviewCard.vue";
import { PRESCRIBER_TEMPLATES, getTemplateVariables } from "@/templates/prescriberTemplates";
import { renderTemplate } from "@/utils/templateRenderer";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const route = useRoute();
const router = useRouter();

// Client config for feature flags
const clientConfig = ref(null);
const sendEmailEnabled = computed(() => clientConfig.value?.uiConfig?.intake?.sendEmailEnabled ?? false);

const nonEditableStatuses = [
  "Created",
  "Processing",
  "Sent to prescriber",
  "Sent to plan",
  "No payor response received",
  "Requires more information",
  "Approved",
  "Prior authorization not required",
  "Denied",
  "Archived",
];

const isDisabled = computed(() => {
  if (orderInfo.value?.status) {
    return nonEditableStatuses.includes(orderInfo.value.status);
  }
  return false;
});

const preloader = ref(false);
const pageTitle = ref("Review Submission");
const faxDestinationOptions = ref([]);
const faxAttachments = ref([]);
const showSuccessNotification = ref(false);
const auditLogListRef = ref(null);
const allFormStepsRef = ref(null);
const outgoingFaxEditorRef = ref(null);
const filledFormPdfBlob = ref(null);
const errors = ref([]);
const showFaxEditor = ref(false);
const showEmailEditor = ref(false);
const currentFaxAction = ref(null); // 'prescriber' or 'plan'
const currentFaxNumber = ref(null);
const currentTemplate = ref(null);
const sendButtonText = ref("Send Fax");
const showFaxHelpText = ref(false);
const outgoingEmailEditorRef = ref(null);
const currentEmailAddresses = ref([]);
const currentEmailSubject = ref("");
const sendMethod = ref("fax"); // 'fax' or 'email'
const successNotificationMessage = ref("Fax sent successfully!");

const emailSendButtonText = computed(() => {
  return "Send Email";
});

const attachmentOptions = computed(() => {
  const options = [];

  // Response faxes from faxAttachments
  faxAttachments.value.forEach((a, index) => {
    options.push({
      title: `Response Fax ${index + 1}: ${a.label} ${
        a.timestamp ? "Received on " + convertDateFormat(a.timestamp) : ""
      }`,
      value: {
        type: "response_fax",
        s3_url: a.link,
      },
    });
  });

  // Flattened form (for OrderInfo only)
  if (
    orderInfo.value?.editableSubmissionForm &&
    orderInfo.value?.submissionMethod === "eFax" &&
    orderInfo.value?.orderKey
  ) {
    options.push({
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    });
  }

  return options;
});

const supportingDocuments = computed(() => {
  const urls = [];

  // Patient chart URLs: orderInfo.patientChart[].links[0] (if available)
  if (orderInfo.value?.patientChart && Array.isArray(orderInfo.value.patientChart)) {
    orderInfo.value.patientChart.forEach((chart) => {
      if (chart.links && Array.isArray(chart.links) && chart.links.length > 0) {
        urls.push(chart.links[0]);
      }
    });
  }

  // Medical necessity URLs: orderInfo.medicalNecessity (if available)
  if (orderInfo.value?.medicalNecessity && Array.isArray(orderInfo.value.medicalNecessity)) {
    orderInfo.value.medicalNecessity.forEach((url) => {
      if (url) {
        urls.push(url);
      }
    });
  }

  return urls;
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info"];
const reviewStatuses = ["Requires review", "Review clinical info"];

const getTemplateForAction = (submissionMethod, action) => {
  if (!submissionMethod) return null;

  let templateType = null;
  if (action === "prescriber") {
    if (submissionMethod === "CoverMyMeds") {
      templateType = "COVERMYMEDS";
    } else if (submissionMethod === "eFax") {
      templateType = "FAX";
    } else {
      templateType = "MANUAL";
    }
  } else if (action === "plan") {
    // For "Send to plan", no template needed - only filled form attachment
    return null;
  }

  if (!templateType) return null;

  const template = PRESCRIBER_TEMPLATES[templateType];
  if (!template) return null;

  const variables = getTemplateVariables(orderInfo.value);
  const rendered = renderTemplate(template.content || "", variables);

  return {
    name: template.name,
    content: rendered,
    category: "prescriberTemplates",
  };
};

const preselectedFilledFormAttachment = ref(null);

onBeforeMount(async () => {
  if (route.params.id) {
    clearOrderInfoStorage();
    loadOrder(route.params.id);
  }
  getFaxForms();
  startStaffTime(route.params.id);
  window.addEventListener("beforeunload", onBeforeUnload);
  // Fetch client config for feature flags
  try {
    clientConfig.value = await mainServices.getCurrentUserClientConfig();
  } catch (e) {
    // Suppress - email feature will be disabled if config fails to load
  }
});

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

async function loadOrder(orderKey) {
  preloader.value = true;
  errors.value = [];
  try {
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    orderInfo.value = fetchedOrder.orderInfo;
    faxAttachments.value = fetchedOrder.orderInfo.orderSubmission.responseDocuments || [];
    pageTitle.value = incompleteStatuses.includes(orderInfo.value.status)
      ? "Review Submission and Send to Processing"
      : reviewStatuses.includes(orderInfo.value.status)
      ? "Review Submission and Send to Plan"
      : "Review Submission";

    // Fetch filled form PDF if submission is not editable and has editableSubmissionForm
    if (orderInfo.value?.editableSubmissionForm && nonEditableStatuses.includes(orderInfo.value.status)) {
      try {
        const pdfResponse = await orderInfoService.getFilledFormPdf(orderKey);
        filledFormPdfBlob.value = pdfResponse.data;
      } catch (err) {
        // Suppress error - filled form might not be available yet
      }
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

const onBeforeUnload = () => {
  updateStaffTime();
};

onBeforeRouteLeave(async () => {
  await updateStaffTime();
});

const startStaffTime = (orderKey) => {
  try {
    orderInfoService.startOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async () => {
  const orderKey = route.params.id;
  try {
    window.removeEventListener("beforeunload", onBeforeUnload);
    await orderInfoService.pushOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const saveOrderDraft = async (resolve, reject) => {
  try {
    // For "Send to plan" action, save orderInfo with formStep="Send to Plan" before sending fax
    if (currentFaxAction.value === "plan") {
      // Ensure PDF form is merged if needed
      if (orderInfo.value.editableSubmissionForm && allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }

      // Save orderInfo with formStep="Send to Plan" (same as performSubmission)
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }

      const orderData = {
        ...orderInfo.value,
        formStep: "Send to Plan",
      };

      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderData);
    } else {
      // For other actions (prescriber), just save draft
      if (allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }
    }
    resolve();
  } catch (error) {
    reject(error);
  }
};

const preselectFilledFormAttachment = () => {
  // Pre-select filled form attachment (used for both prescriber and plan actions)
  if ((orderInfo.value?.editableSubmissionForm && orderInfo.value?.orderKey) || filledFormPdfBlob.value) {
    preselectedFilledFormAttachment.value = {
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    };
  }
};

const handlePrescriberTemplateSelected = () => {
  // When prescriber template is selected, pre-select the attachment if eFax
  if (orderInfo.value?.submissionMethod === "eFax") {
    preselectFilledFormAttachment();
  }
};

const onFaxComplete = async ({ sent, isFrontendTemplate, frontendTemplateCategory }) => {
  if (sent) {
    successNotificationMessage.value = "Fax sent successfully!";
    showSuccessNotification.value = true;
    // Refresh audit log to show the new fax entry
    if (auditLogListRef.value) {
      auditLogListRef.value.refresh();
    }

    // Handle "Send to plan" action - redirect after fax is sent
    if (currentFaxAction.value === "plan") {
      // Clear and redirect like performSubmission does
      errors.value = [];
      clearOrderInfoStorage();
      router.push({ name: "all-submissions" });
      window.scrollTo(0, 0);
      return;
    }

    // Handle frontend template send event for prescriber action
    if (isFrontendTemplate && frontendTemplateCategory) {
      if (frontendTemplateCategory === "prescriberTemplates" && currentFaxAction.value === "prescriber") {
        // Use dedicated endpoint to handle send to prescriber flow and restart polling
        try {
          const orderData = orderInfo.value ? { order_data: orderInfo.value } : {};
          await orderInfoService.sendToPrescriber(orderInfo.value.orderKey, orderData);
          // Refresh the orderInfo to get the updated status
          await loadOrder(orderInfo.value.orderKey);
        } catch (e) {
          // Suppress
        }
      }
    }
  }
};

const onEmailComplete = async ({ sent, isFrontendTemplate, frontendTemplateCategory }) => {
  if (sent) {
    successNotificationMessage.value = "Email sent successfully!";
    showSuccessNotification.value = true;
    // Refresh audit log to show the new email entry
    if (auditLogListRef.value) {
      auditLogListRef.value.refresh();
    }

    // Handle "Send to plan" action - redirect after email is sent
    if (currentFaxAction.value === "plan") {
      // Clear and redirect like performSubmission does
      errors.value = [];
      clearOrderInfoStorage();
      router.push({ name: "all-submissions" });
      window.scrollTo(0, 0);
      return;
    }

    // Handle frontend template send event for prescriber action
    if (isFrontendTemplate && frontendTemplateCategory) {
      if (frontendTemplateCategory === "prescriberTemplates" && currentFaxAction.value === "prescriber") {
        // Use dedicated endpoint to handle send to prescriber flow and restart polling
        try {
          const orderData = orderInfo.value ? { order_data: orderInfo.value } : {};
          await orderInfoService.sendToPrescriber(orderInfo.value.orderKey, orderData);
          // Refresh the orderInfo to get the updated status
          await loadOrder(orderInfo.value.orderKey);
        } catch (e) {
          // Suppress
        }
      }
    }
  }
};

const showFaxEditorForAction = async (action) => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Determine if fax editor should be shown based on submission method and action
  if (action === "plan" && submissionMethod !== "eFax") {
    // For "Send to plan", only show fax editor if submission method is eFax
    return;
  }

  currentFaxAction.value = action;

  if (action === "prescriber") {
    currentTemplate.value = getTemplateForAction(submissionMethod, action);
    currentFaxNumber.value = orderInfo.value?.providerContactFax || "";
    sendButtonText.value = "Send fax to prescriber";
    showFaxHelpText.value = false;
    // Set email subject for prescriber
    const prescriberPatientName = `${orderInfo.value?.patientFirstName || ""} ${
      orderInfo.value?.patientLastName || ""
    }`.trim();
    currentEmailSubject.value = `Prior Authorization Request - ${prescriberPatientName}`.trim();
    // Provider email if available
    currentEmailAddresses.value = orderInfo.value?.providerEmail ? [orderInfo.value.providerEmail] : [];
  } else if (action === "plan") {
    // For "plan" action, no template - only filled form attachment
    currentTemplate.value = null;
    currentFaxNumber.value = orderInfo.value?.sendFaxFormTo || "";
    sendButtonText.value = "Send fax to payor";
    showFaxHelpText.value = true;
    // Set email subject for plan/payor
    const planPatientName = `${orderInfo.value?.patientFirstName || ""} ${
      orderInfo.value?.patientLastName || ""
    }`.trim();
    currentEmailSubject.value = `Prior Authorization Submission - ${planPatientName}`.trim();
    // Email addresses would come from referral source once available
    currentEmailAddresses.value = orderInfo.value?.referralSourceEmail ? [orderInfo.value.referralSourceEmail] : [];

    // Pre-select filled form attachment for "Send to plan"
    preselectFilledFormAttachment();
  }

  showFaxEditor.value = true;
  showEmailEditor.value = sendEmailEnabled.value;

  // Wait for next tick to ensure component is rendered
  await nextTick();

  // Scroll to the editor component
  const editorRef = sendMethod.value === "email" ? outgoingEmailEditorRef : outgoingFaxEditorRef;
  if (editorRef.value?.$el) {
    editorRef.value.$el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const handleSendToPrescriber = async () => {
  await showFaxEditorForAction("prescriber");
};

const handleSendToPlan = async () => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Only intercept if submission method is eFax
  if (submissionMethod === "eFax") {
    await showFaxEditorForAction("plan");
  }
  // For other submission methods, submission proceeds normally in AllFormStepsView
};
</script>

<style lang="scss" scoped></style>
